import { useApiClient } from "@brewardapp/api-client";
import { useStore } from "@breward/store";
import { Button, Loader, TextInput, Toggle } from "@breward/ui";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const RegisterForm = ({ redirectPath }) => {
    const { register, handleSubmit, control, formState, watch } = useForm();
    const navigate = useNavigate();
    const [t] = useTranslation();
    const showNotification = useStore((state) => state.showNotification);

    const api = useApiClient()
    const registerUser = api.users.useCreateUser();

    const password = watch('password');

    const onSubmit = (values) => {
        registerUser.mutate(values, {
            onSuccess: () => {
                showNotification({
                    type: "success",
                    title: t(
                        "Notifications:ParticipantWeb.userSignedUp.title",
                        "Signed up"
                    ),
                    message: t(
                        "Notifications:ParticipantWeb.userSignedUp.message",
                        "Welcome in Breward."
                    ),
                    "data-test-id": "registration-success-message"
                });
                navigate(redirectPath);
            },
            onError: (error) => {
                showNotification({
                    type: "error",
                    title: error?.errors?.[0]?.title,
                    message: error?.errors?.[0]?.detail,
                    "data-test-id": "error-notification"
                });
            },
        });
    };

    return (
        <div className="">
            {registerUser?.isLoading ? (
                <Loader />
            ) : (
                <>
                    <TextInput
                        required
                        data-test-id="first-name"
                        error={formState?.errors?.firstName?.message}
                        containerClassName="mb-2"
                        autoComplete="firstName"
                        label={t("global:shared.firstNameLabel", "First Name")}
                        {...register("firstName", {
                            required: t("global:shared.requiredField", "Pole wymagane"),
                        })}
                    />
                    <TextInput
                        required
                        data-test-id="last-name"
                        error={formState?.errors?.lastName?.message}
                        containerClassName="mb-2"
                        autoComplete="lastName"
                        label={t("global:shared.lastNameLabel", "Last Name")}
                        {...register("lastName", {
                            required: t("global:shared.requiredField", "Pole wymagane"),
                        })}
                    />
                    <TextInput
                        data-test-id="email"
                        required
                        containerClassName="mb-2"
                        type="email"
                        autoComplete="email"
                        label={t("global:shared.emailLabel", "Email")}
                        error={formState?.errors?.email?.message}
                        {...register("email", {
                            pattern: {
                                value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                                message: t(
                                    "global:shared.invalidEmail",
                                    "Invalid email format"
                                ),
                            },
                            required: t("global:shared.requiredField", "Pole wymagane"),
                        })}
                    />
                    <TextInput
                        data-test-id="password"
                        required
                        containerClassName="mb-2"
                        autoComplete="password"
                        label={t("global:shared.passwordLabel", "Password")}
                        type="password"
                        error={formState?.errors?.password?.message}
                        {...register("password", {
                            required: t("global:shared.requiredField", "Pole wymagane"),
                        })}
                    />
                    <TextInput
                        data-test-id="password-confirmation"
                        required
                        containerClassName="mb-2"
                        label={t(
                            "global:shared.passwordConfirmationLabel",
                            "Password Confirmation"
                        )}
                        type="password"
                        error={formState?.errors?.passwordConfirmation?.message}
                        {...register("passwordConfirmation", {
                            required: t("global:shared.requiredField", "Pole wymagane"),
                            validate: (value) =>
                                value === password || t("global:shared.passwordMismatch", "Passwords do not match"),
                        })}
                    />
                    <div className="mt-2">
                        <Controller
                            control={control}
                            name={"termsAndConditions"}
                            rules={{
                                required: t(
                                    "global:shared.requiredConsent",
                                    "You must agree with terms and privacy policy."
                                ),
                            }}
                            render={({ field: { onChange, value } }) => (
                                <Toggle
                                    data-test-id="terms-and-conditions"
                                    required
                                    error={formState?.errors?.termsAndConditions?.message}
                                    label={
                                        <Trans
                                            i18nKey={"global:shared.termsAndPrivacyLabel"}
                                            components={{
                                                termsLink: (
                                                    <a
                                                        className="text-accent font-semibold underline"
                                                        href="https://breward.app/terms"
                                                    />
                                                ),
                                                privacyLink: (
                                                    <a
                                                        className="text-accent font-semibold underline"
                                                        href="https://breward.app/privacy"
                                                    />
                                                ),
                                            }}
                                        />
                                    }
                                    onToggle={onChange}
                                    value={value}
                                />
                            )}
                        />
                    </div>
                    <Button
                        data-test-id="register-button"
                        onClick={() => handleSubmit(onSubmit)()}
                        className="mt-6 text-center text-lg uppercase"
                    >
                        {t("global:shared.register", "Sign up")}
                    </Button>
                </>
            )}
        </div>
    );
};

export default RegisterForm;
